import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase/config';
import { JIG_CONSENT_VERSION, JIG_CONSENT_TEXT } from '../PrivacyPolicy';
import { sha256Hex } from '../../config/careLegal';

// 🔐 지그 첫 이용 동의(법무 §3, 외주 재위탁 동의 방식 준용) — /jig(Jig.js)·/jig-result(JigWeb.js) 공용.
//    지그 결과(jigResults = 환자정보·다운로드)를 보기 전에 본인 계정에 1회 동의를 기록한다.
//    기록 = users/{행위자 본인 uid}.consents.jig = { agreed, version(jig-1.0), agreedAt, textHash(문구판) }.
//      · 가입 경로 무관(대표·직원·해외 각자 본인 문서). consents 는 firestore.rules users update 1번 절 blacklist 밖이라
//        본인 쓰기 허용(DelegationV70ReconfirmModal 선례, rules 변경 없음).
//    확인/기록 실패 = fail-closed(잠금 유지, console.error, 조용히 열지 않음). 동의가 없으면 환자정보·다운로드를 열지 않는다
//    ("기록 안 되면 보관 켤 수 없다"를 UX로 강제).

export function useJigConsent() {
    const uid = auth.currentUser?.uid;
    const [state, setState] = useState({ loading: true, agreed: false, error: '' });
    const [check, setCheck] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    // 본인 계정의 지그 동의 여부 확인 — 실패 시 잠금 유지
    useEffect(() => {
        if (!uid) { setState({ loading: false, agreed: false, error: '' }); return undefined; }
        let alive = true;
        setState({ loading: true, agreed: false, error: '' });
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', uid));
                if (!alive) return;
                const c = snap.exists() ? (snap.data()?.consents?.jig) : null;
                setState({ loading: false, agreed: !!(c?.agreed && c?.version === JIG_CONSENT_VERSION), error: '' });
            } catch (e) {
                if (!alive) return;
                console.error('[지그] 지그 이용 동의 상태 확인 실패 — 잠금 유지:', e?.code || e?.message || e);
                setState({ loading: false, agreed: false, error: '동의 상태를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.' });
            }
        })();
        return () => { alive = false; };
    }, [uid]);

    // 동의 기록: version(jig-1.0)·일시·계정(본인 문서)·문구판(textHash)
    const agree = async () => {
        if (busy || !uid || !check) return;
        setBusy(true); setErr('');
        try {
            const agreedAt = new Date().toISOString();
            let textHash = null;
            try { textHash = (await sha256Hex(JIG_CONSENT_TEXT)) || null; }
            catch (e) { console.error('[지그] 동의 문구 해시 계산 실패(동의는 기록):', e?.message || e); }
            await updateDoc(doc(db, 'users', uid), {
                'consents.jig': { agreed: true, version: JIG_CONSENT_VERSION, agreedAt, textHash },
                updatedAt: agreedAt,
            });
            setState({ loading: false, agreed: true, error: '' });
        } catch (e) {
            console.error('[지그] 지그 이용 동의 기록 실패:', e?.code || e?.message || e);
            setErr('동의를 기록하지 못했습니다. 잠시 후 다시 눌러 주세요.');
        } finally { setBusy(false); }
    };

    return { uid, loading: state.loading, agreed: state.agreed, error: state.error, check, setCheck, busy, err, agree };
}

// 동의 전 화면(환자정보·다운로드 대신 노출). padded=상위에 body 패딩이 없으면 자체 패딩.
export function JigConsentGate({ consent, padded = true }) {
    return (
        <div style={padded ? S.wrapPadded : S.wrap}>
            <div style={S.card}>
                <div style={S.title}>{'지그 결과 보관에 대한 동의'}</div>
                <p style={S.text}>{JIG_CONSENT_TEXT}</p>
                <label style={S.row}>
                    <input type="checkbox" checked={consent.check} onChange={e => consent.setCheck(e.target.checked)} />
                    <span>{'위 내용에 동의합니다.'}</span>
                </label>
                {(consent.err || consent.error) && <div style={S.err}>{consent.err || consent.error}</div>}
                <button type="button" onClick={consent.agree} disabled={!consent.check || consent.busy}
                    style={{ ...S.btn, opacity: (!consent.check || consent.busy) ? 0.5 : 1 }}>
                    {consent.busy ? '기록 중…' : '동의하고 계속'}
                </button>
            </div>
        </div>
    );
}

const S = {
    wrap: { display: 'flex', flexDirection: 'column' },
    wrapPadded: { padding: 20 },
    card: {
        display: 'flex', flexDirection: 'column', gap: 12, padding: '18px 20px', maxWidth: 680,
        backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    },
    title: { fontSize: 14, fontWeight: 700, color: '#0f766e' },
    text: { fontSize: 13, color: '#334155', lineHeight: 1.7, margin: 0, wordBreak: 'keep-all' },
    row: { display: 'inline-flex', alignItems: 'flex-start', gap: 8, fontSize: 13.5, color: '#1e293b', cursor: 'pointer', lineHeight: 1.6 },
    err: { fontSize: 12.5, color: '#8a2020', background: '#fdecec', border: '1px solid #f5cfcf', borderRadius: 8, padding: '8px 11px' },
    btn: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', alignSelf: 'flex-start',
        backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: 8,
        fontSize: 13, fontWeight: 600, cursor: 'pointer',
    },
};
